import type { FunnelFacts, RecentEvent } from '../types';
import { planName } from '../insights/account-words';
import { campaignWord, spanWord } from '../funnel/funnel-words';
import { deviceWord, featureWord, screenWord } from '../usage/words';

/** One line of the customer's journey: what happened, when, and how long after the line before. */
export interface Milestone {
  key: string;
  text: string;
  at: string | null;
  /** From the previous reached milestone: "2 h later"; null for the first or a step not reached. */
  elapsed: string | null;
  state: 'done' | 'pending' | 'quiet';
}

const QUIET_AFTER_MS = 7 * 24 * 3600 * 1000;

const pagesSourceWords: Record<string, string> = {
  upload: 'upload',
  scan: 'scan',
  email: 'email',
  api: 'the API',
  folder: 'a watched folder',
};

function pagesSourceWord(source: string | null): string {
  if (!source) return 'an unknown route';
  return pagesSourceWords[source] ?? source;
}

function seconds(from: string, to: string): number {
  return Math.max(0, (new Date(to).getTime() - new Date(from).getTime()) / 1000);
}

function step(key: string, done: string, notYet: string, at: string | null): Milestone {
  return at
    ? { key, text: done, at, elapsed: null, state: 'done' }
    : { key, text: notYet, at: null, elapsed: null, state: 'pending' };
}

function quietLine(lastSeenAt: string | null, now: number): Milestone | null {
  if (!lastSeenAt || now - new Date(lastSeenAt).getTime() < QUIET_AFTER_MS) return null;
  return {
    key: 'quiet',
    text: `Went quiet: nothing for ${spanWord((now - new Date(lastSeenAt).getTime()) / 1000)}`,
    at: lastSeenAt,
    elapsed: null,
    state: 'quiet',
  };
}

/**
 * The company's story in order: signed up from where, first job, first pages and how, first
 * export, plan chosen, and a "went quiet" line when nothing has happened for a week. Each reached
 * step says how long after the step before it came.
 */
export function journeyMilestones(
  j: FunnelFacts,
  lastSeenAt: string | null,
  now: number = Date.now(),
): Milestone[] {
  const from = j.source === 'direct' ? 'directly' : `from ${campaignWord(j.source, j.campaign)}`;
  const lines: Milestone[] = [
    { key: 'signedUp', text: `Signed up ${from}`, at: j.signedUpAt, elapsed: null, state: 'done' },
    step('firstJob', 'Created the first job', 'No job yet', j.firstJobAt),
    step(
      'firstPages',
      `First pages arrived by ${pagesSourceWord(j.firstPagesSource)}`,
      'No pages yet',
      j.firstPagesAt,
    ),
    step('firstExport', 'First export delivered', 'Nothing exported yet', j.firstExportAt),
    step('plan', 'Chose a plan', 'No plan chosen yet', j.planChosenAt),
  ];
  let previous = j.signedUpAt;
  for (const line of lines.slice(1)) {
    if (!line.at) continue;
    line.elapsed = `${spanWord(seconds(previous, line.at))} later`;
    previous = line.at;
  }
  const quiet = quietLine(lastSeenAt, now);
  return quiet ? [...lines, quiet] : lines;
}

function detailText(detail: Record<string, unknown> | null, key: string): string | null {
  const v = detail?.[key];
  return typeof v === 'string' || typeof v === 'number' ? String(v) : null;
}

function on(screen: string | null): string {
  return screen ?? 'a screen';
}

function refusedWords(e: RecentEvent, screen: string | null): string {
  const title = detailText(e.detail, 'title');
  return `Was refused on ${on(screen)}${title ? `: ${title}` : ''}`;
}

function pagesAddedWords(e: RecentEvent): string {
  const pages = detailText(e.detail, 'pages');
  const by = pagesSourceWord(detailText(e.detail, 'source'));
  return pages ? `Added ${pages} pages by ${by}` : `Added pages by ${by}`;
}

function sessionWords(e: RecentEvent): string {
  const device = deviceWord(detailText(e.detail, 'device') ?? 'unknown').toLowerCase();
  return `Started a session on a ${device}`;
}

/** Stripe's feedback choices and reasons (cancellation_details) as a person says them. */
const cancelWhyWords: Record<string, string> = {
  too_expensive: 'too expensive',
  missing_features: 'missing features',
  switched_service: 'switched to another service',
  unused: 'not using it',
  customer_service: 'customer service',
  too_complex: 'too complex',
  low_quality: 'quality',
  other: 'other',
  payment_failed: 'payment failed',
  payment_disputed: 'payment disputed',
  cancellation_requested: 'on request',
};

const COMMENT_MAX = 120;

/**
 * "Cancelled Pro: too expensive, 'Budget cut for Q4'": the package, the customer's feedback choice
 * (else Stripe's reason) and their comment, which billing.cancelled carries (round 3, marketing
 * should 1). The comment is cut at 120 characters; a cancellation with none of it says "Cancelled a plan".
 */
function cancelledWords(e: RecentEvent): string {
  const pkg = detailText(e.detail, 'package');
  const head = pkg ? `Cancelled ${planName(pkg)}` : 'Cancelled a plan';
  const code = detailText(e.detail, 'feedback') ?? detailText(e.detail, 'reason');
  const why = code ? (cancelWhyWords[code] ?? code.replace(/_/g, ' ')) : null;
  const raw = detailText(e.detail, 'comment')?.trim();
  const comment = raw
    ? `'${raw.length > COMMENT_MAX ? `${raw.slice(0, COMMENT_MAX - 1).trimEnd()}…` : raw}'`
    : null;
  const parts = [why, comment].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? `${head}: ${parts.join(', ')}` : head;
}

const special: Record<string, (e: RecentEvent, screen: string | null) => string> = {
  'screen.viewed': (_e, screen) => `Opened ${on(screen)}`,
  'screen.left': (_e, screen) => `Left ${on(screen)}`,
  'browser.error': (_e, screen) => `Saw an error on ${on(screen)}`,
  'session.started': sessionWords,
  'request.failed': refusedWords,
  'pages.added': pagesAddedWords,
  'billing.cancelled': cancelledWords,
};

/** A recent event as a sentence a person would say; never the event name or a route pattern. */
export function eventWords(e: RecentEvent): string {
  const screen = e.screen ? screenWord(e.screen) : null;
  const say = special[e.name];
  if (say) return say(e, screen);
  const did = featureWord(e.feature ?? e.name);
  return screen ? `${did} on ${screen}` : did;
}
